import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  Send,
  X,
  RotateCcw,
  Sparkles,
  Car,
  Wrench,
  Clock,
  ExternalLink,
  ShieldCheck,
  AlertCircle,
  HelpCircle,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTenantContext } from '@/providers/TenantContextProvider';
import { toast } from '@/hooks/useToast';
import { conversationalService } from '../../../services/conversational.service';
import { saasService } from '../../../services/saas.service';
import type {
  ConversationalMessage,
  ActionCitation,
  ConversationContextState,
} from '../../../types';

function conversationalErrorMessage(err: any): string {
  const detailsCode = String(err?.details?.code || '');
  const message = String(err?.message || '');
  const blob = `${detailsCode} ${message} ${err?.code || ''}`;
  if (blob.includes('ARLO_KEY_NOT_CONFIGURED') || blob.includes('chave Gemini gerenciada')) {
    return 'A chave Gemini da Arlo ainda não foi configurada. O administrador da plataforma precisa cadastrá-la em Governança de IA.';
  }
  if (blob.includes('QUOTA_EXCEEDED') || blob.includes('Franquia')) {
    return 'Franquia mensal de IA atingida. As demais funcionalidades da plataforma continuam operando normalmente.';
  }
  if (blob.includes('CIRCUIT_BREAKER') || blob.includes('pausado')) {
    return 'O serviço de IA está temporariamente pausado pela plataforma.';
  }
  if (blob.includes('AI_DISABLED')) {
    return 'A inteligência artificial está desabilitada para este cliente.';
  }
  if (message && !message.toLowerCase().includes('internal')) return message;
  return 'Não foi possível processar sua mensagem neste momento. Tente novamente em instantes.';
}

const SUGGESTIONS = [
  { label: 'Onde está o veículo placa...', query: 'Onde está o veículo placa ' },
  { label: 'Veículos aguardando peças', query: 'Quais veículos estão aguardando peças de reposição?' },
  { label: 'Veículos estagnados (>48h)', query: 'Quais veículos estão parados ou estagnados há mais de 48 horas?' },
  { label: 'Resumo da operação hoje', query: 'Qual o resumo geral da operação do pátio hoje?' },
  { label: 'Ordens de serviço abertas', query: 'Quais ordens de serviço estão em andamento ou atrasadas?' },
];

export const ConversationalDrawer: React.FC = () => {
  const { appUser } = useAuth();
  const { tenant } = useTenantContext();
  const tenantId = tenant?.id || appUser?.tenantId;
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState<ConversationalMessage[]>([]);
  const [conversationContext, setConversationContext] = useState<ConversationContextState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      inputRef.current?.focus();
    }
  }, [isOpen, messages]);

  const handleSendMessage = async (textToSend?: string) => {
    const text = (textToSend || inputMessage).trim();
    if (!text || !tenantId || isLoading) return;

    const userMsg: ConversationalMessage = {
      id: `usr_${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await conversationalService.sendMessage({
        tenantId,
        message: text,
        conversationId: conversationContext?.conversationId,
        context: conversationContext ? {
          activeVehicleId: conversationContext.activeVehicleId,
          activePlate: conversationContext.activePlate,
        } : undefined,
      });

      setMessages((prev) => [...prev, response.message]);
      setConversationContext(response.context);
    } catch (err: any) {
      console.error('Erro na conversa:', err);
      setErrorMessage(conversationalErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async () => {
    if (tenantId && conversationContext?.conversationId) {
      await conversationalService.resetSession(tenantId, conversationContext.conversationId).catch(() => null);
    }
    setMessages([]);
    setConversationContext(null);
    setErrorMessage(null);
  };

  const handleNavigateCitation = (citation: ActionCitation) => {
    setIsOpen(false);
    navigate(citation.url);
  };

  return (
    <>
      {tenantId && (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-label="Abrir Assistente Operacional OpsCopilot"
      >
        <div className="relative">
          <Bot className="w-5 h-5" />
          <span className="absolute -top-1 -right-1 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
        </div>
        <span className="font-semibold text-sm tracking-wide hidden sm:inline">OpsCopilot</span>
      </button>
      )}

      {/* Drawer Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Slide-over Panel */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-full sm:w-[480px] bg-slate-900 border-l border-slate-800 shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 bg-slate-950/80 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400">
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-slate-100 text-base">OpsCopilot</h3>
                <span className="text-[10px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                  Tool Calling
                </span>
              </div>
              <p className="text-xs text-slate-400">Operações & Inteligência em Linguagem Natural</p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={handleReset}
              title="Nova conversa / Limpar sessão"
              className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 rounded-lg transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Message Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="py-6 px-2 text-center space-y-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 mx-auto flex items-center justify-center text-indigo-400">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-200 text-sm">Como posso apoiar sua operação hoje?</h4>
                <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
                  Pergunte sobre localização de veículos, peças faltantes, avarias, tempo de pátio ou resumo do dia.
                </p>
              </div>

              {/* Suggestions */}
              <div className="pt-2 space-y-2 text-left">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 px-1">Sugestões rápidas:</p>
                {SUGGESTIONS.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      if (s.query.endsWith('placa ')) {
                        setInputMessage(s.query);
                        inputRef.current?.focus();
                      } else {
                        handleSendMessage(s.query);
                      }
                    }}
                    className="w-full text-left p-2.5 rounded-xl bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 hover:border-blue-500/30 text-xs text-slate-300 hover:text-white transition-all flex items-center justify-between group"
                  >
                    <span>{s.label}</span>
                    <Sparkles className="w-3.5 h-3.5 text-slate-500 group-hover:text-blue-400 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}
              >
                <div
                  className={`max-w-[88%] rounded-2xl px-4 py-3 text-xs leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-none shadow-md'
                      : 'bg-slate-800/90 text-slate-200 border border-slate-700/60 rounded-bl-none shadow-sm'
                  }`}
                >
                  {m.content}

                  {/* Grounded Badge */}
                  {m.grounded && (
                    <div className="mt-2.5 pt-2 border-t border-slate-700/50 flex items-center gap-1.5 text-[10px] text-emerald-400">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      <span>Resposta baseada em dados operacionais auditados</span>
                    </div>
                  )}

                  {/* Action Citations */}
                  {m.citations && m.citations.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-slate-700/50 space-y-1.5">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Ações Relacionadas:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {m.citations.map((c, i) => (
                          <button
                            key={i}
                            onClick={() => handleNavigateCitation(c)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 border border-blue-500/30 text-[11px] font-medium transition-colors"
                          >
                            {c.type === 'VEHICLE_360' && <Car className="w-3 h-3 text-blue-400" />}
                            {c.type === 'SERVICE_ORDER' && <Wrench className="w-3 h-3 text-amber-400" />}
                            {c.type === 'INSPECTION' && <Clock className="w-3 h-3 text-purple-400" />}
                            <span>{c.label}</span>
                            <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between w-full max-w-[88%] mt-1 px-1">
                  <span className="text-[10px] text-slate-500">
                    {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {m.role === 'assistant' && tenantId && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={async () => {
                          await saasService.submitConversationFeedback({
                            tenantId,
                            conversationId: conversationContext?.conversationId || 'default',
                            messageId: m.id,
                            rating: 'HELPFUL',
                          });
                          toast.success('Obrigado pelo feedback!');
                        }}
                        title="Resposta útil"
                        className="p-1 text-slate-500 hover:text-emerald-400 hover:bg-slate-800 rounded transition-colors"
                      >
                        <ThumbsUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={async () => {
                          await saasService.submitConversationFeedback({
                            tenantId,
                            conversationId: conversationContext?.conversationId || 'default',
                            messageId: m.id,
                            rating: 'UNHELPFUL',
                          });
                          toast.success('Feedback registrado para melhoria.');
                        }}
                        title="Resposta não útil"
                        className="p-1 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded transition-colors"
                      >
                        <ThumbsDown className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}

          {/* Loading Indicator */}
          {isLoading && (
            <div className="flex items-center gap-2 p-3.5 rounded-2xl bg-slate-800/60 border border-slate-700/40 text-slate-400 text-xs w-fit">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
              </div>
              <span className="ml-1 text-[11px]">Consultando ferramentas operacionais...</span>
            </div>
          )}

          {/* Error Banner */}
          {errorMessage && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-3 border-t border-slate-800 bg-slate-950/80">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="flex items-center gap-2"
          >
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Digite sua dúvida sobre a operação..."
              disabled={isLoading}
              className="flex-1 bg-slate-900 border border-slate-700/70 focus:border-blue-500 rounded-xl px-3.5 py-2.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 transition-colors"
            />
            <button
              type="submit"
              disabled={!inputMessage.trim() || isLoading}
              className="p-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:hover:bg-blue-600 text-white rounded-xl shadow transition-colors flex items-center justify-center shrink-0"
              aria-label="Enviar mensagem"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 px-1">
            <span>OpsCopilot • 1 AI Credit por consulta</span>
            <span className="flex items-center gap-1">
              <HelpCircle className="w-3 h-3" /> Respostas grounded
            </span>
          </div>
        </div>
      </div>
    </>
  );
};
